import { useEffect, useMemo, useState } from 'react'

import { isHeicUrl, isPdfUrl } from './rotation'

export type FileStatus = 'ready' | 'converting' | 'error'

export type ViewerFile = {
  /** Original URL. The rotation key — never render this for HEIC. */
  src: string
  /** What to actually render. null while converting or after a failure. */
  displaySrc: string | null
  isPdf: boolean
  status: FileStatus
}

export const useDisplaySources = (browserFileNames: string[]): ViewerFile[] => {
  const [converted, setConverted] = useState<Record<string, string>>({})
  const [failed, setFailed] = useState<Record<string, true>>({})

  // Identity of `browserFileNames` is not guaranteed stable by callers, and
  // re-running this effect would re-do megabytes of conversion work on every
  // parent render. Key off the contents instead.
  const key = browserFileNames.join('|')

  useEffect(() => {
    const sources = key ? key.split('|') : []
    const heicSources = sources.filter(isHeicUrl)
    if (!heicSources.length) return

    let cancelled = false
    const createdUrls: string[] = []

    const run = async () => {
      // Lazy: requests with no HEIC never pull in the ~1.4 MB library.
      const { default: heic2any } = await import('heic2any')

      for (const src of heicSources) {
        try {
          const response = await fetch(src)
          const blob = await response.blob()
          const jpeg = await heic2any({ blob, toType: 'image/jpeg', quality: 0.9 })
          const url = URL.createObjectURL(Array.isArray(jpeg) ? jpeg[0] : jpeg)
          createdUrls.push(url)
          if (cancelled) return
          setConverted((prev) => ({ ...prev, [src]: url }))
        } catch {
          if (cancelled) return
          setFailed((prev) => ({ ...prev, [src]: true }))
        }
      }
    }

    void run()

    return () => {
      cancelled = true
      createdUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [key])

  return useMemo(
    () =>
      (key ? key.split('|') : []).map((src): ViewerFile => {
        const isPdf = isPdfUrl(src)

        if (!isHeicUrl(src)) {
          return { src, displaySrc: src, isPdf, status: 'ready' }
        }
        if (failed[src]) {
          return { src, displaySrc: null, isPdf, status: 'error' }
        }
        const url = converted[src]
        return url
          ? { src, displaySrc: url, isPdf, status: 'ready' }
          : { src, displaySrc: null, isPdf, status: 'converting' }
      }),
    [key, converted, failed],
  )
}
